import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext(null);

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(`${siteConfig.brandName}.cart`).filter(
      (item) => Number(item?.id) > 0 && Number(item?.quantity) > 0
    )
  );
  const [wishlist, setWishlist] = useState(() =>
    readStoredArray(`${siteConfig.brandName}.wishlist`)
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0)
  );

  useEffect(() => {
    window.localStorage.setItem(`${siteConfig.brandName}.cart`, JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    window.localStorage.setItem(`${siteConfig.brandName}.wishlist`, JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size = "", color = "") => {
    const normalizedId = Number(productId);
    const normalizedQuantity = Math.max(1, Number(quantity) || 1);

    setCartItems((items) => {
      const matchingItem = items.find(
        (item) =>
          item.id === normalizedId && item.size === size && item.color === color
      );

      if (!matchingItem) {
        return [
          ...items,
          { id: normalizedId, size, color, quantity: normalizedQuantity },
        ];
      }

      return items.map((item) =>
        item.id === normalizedId && item.size === size && item.color === color
          ? { ...item, quantity: item.quantity + normalizedQuantity }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    setCartItems((items) =>
      items.filter(
        (item) =>
          !(item.id === Number(productId) && item.size === size && item.color === color)
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size = "", color = "", quantity = 1) => {
    const normalizedQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) =>
      items.map((item) =>
        item.id === Number(productId) && item.size === size && item.color === color
          ? { ...item, quantity: normalizedQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    const normalizedId = Number(productId);
    setWishlist((items) => (items.includes(normalizedId) ? items : [...items, normalizedId]));
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlist((items) => items.filter((id) => id !== Number(productId)));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const normalizedId = Number(productId);
    setWishlist((items) =>
      items.includes(normalizedId)
        ? items.filter((id) => id !== normalizedId)
        : [...items, normalizedId]
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlist,
      cartCount: cartItems.reduce((total, item) => total + item.quantity, 0),
      addCart: addToCart,
      removeCart: removeFromCart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlist.includes(Number(productId)),
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};
